import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import type { Segment, Person, Entity, EntityType, CruiseDayType, SegmentType, CruiseConfig, TravelPartyMember } from "../../types";
import { CRUISE_DAY_TYPES, isCruiseDay, getSegmentType } from "../../types";
import type { SegmentWithId } from "../../hooks/useSegments";
import { buildNestedSegmentOptions } from "../../utils/segmentTree";
import { dayTypeLabel } from "../../utils/cruiseItinerary";
import { EntityPicker } from "../entities/EntityPicker";
import { RichTextEditor } from "../common/RichTextEditor";
import { WhoWasThere } from "../common/WhoWasThere";
import { segmentParticipantsLookup } from "../../utils/participants";
import { SEGMENT_ROLE_TYPES } from "../../config/peopleRoleTypes";
import { RoleSelect } from "../interactions/PeopleEditor";
import { addDaysToKey, dateFromDayKey, dayKeyOf } from "../../utils/calendarDay";

export interface SegmentFormResult {
  name: string;
  parentSegmentId: string | null;
  entityId: string | null;
  startDate: Date;
  endDate: Date;
  notes: string;
  /** Explicit "who was there", or null to inherit from the parent/trip. */
  participants: string[] | null;
  people: Person[];
  segmentType: SegmentType;
  cruiseConfig: CruiseConfig | null;
  dayType: CruiseDayType | null;
  portName: string | null;
  portEntityId: string | null;
}

/** What an edit touched, beyond what `SegmentFormResult` can say on its own. */
export interface SegmentFormEdits {
  /**
   * Whether any input the dates are built from (the date fields, a cruise's
   * nights, the segment type) was changed from what the form opened with. The
   * submitted dates are rebuilt from the inputs, so comparing them against the
   * stored ones can't tell an untouched date from a moved one.
   */
  datesChanged: boolean;
}

interface SegmentFormProps {
  initial?: Segment;
  onSubmit: (data: SegmentFormResult, edits: SegmentFormEdits) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
  segments?: SegmentWithId[];
  defaultParentSegmentId?: string | null;
  /** Segment IDs to exclude from parent picker (e.g. self + descendants) */
  excludeSegmentIds?: string[];
  /** Prefill the dates of a new segment (ignored when editing an existing one). */
  defaultStartDate?: Date | null;
  defaultEndDate?: Date | null;
  /** Render in cruise itinerary-day mode (single date, day type + port). */
  dayMode?: boolean;
  /** The trip's travel party — omit to hide the "who was there" picker. */
  travelParty?: Record<string, TravelPartyMember>;
}

const SEGMENT_ENTITY_TYPES: EntityType[] = ["HOTEL", "DESTINATION", "PARK", "EVENT", "SHIP", "LOCATION"];

const SEGMENT_TYPE_OPTIONS: { value: SegmentType; label: string }[] = [
  { value: "general", label: "General grouping" },
  { value: "cruise", label: "Cruise" },
  { value: "themepark", label: "Theme park" },
  { value: "travel", label: "Travel day" },
];

/** A Date as the `yyyy-mm-dd` a date input wants, read in the local zone so
 *  "today" is the day the traveler is actually having. */
function toDateInput(d: Date | null | undefined): string {
  if (!d) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function addDaysToInput(dateStr: string, days: number): string {
  if (!dateStr) return "";
  return addDaysToKey(dateStr, days);
}

export function SegmentForm({ initial, onSubmit, onCancel, submitLabel = "Create Segment", segments = [], defaultParentSegmentId = null, excludeSegmentIds = [], defaultStartDate = null, defaultEndDate = null, dayMode = false, travelParty = {} }: SegmentFormProps) {
  const { userProfile } = useAuth();
  const canManageEntities = !!userProfile?.roles?.includes("manage_entities");
  const isDay = dayMode || isCruiseDay(initial);

  const [name, setName] = useState(initial?.name || "");
  const [nameTouched, setNameTouched] = useState(!!initial?.name);
  const [segmentType, setSegmentType] = useState<SegmentType>(isDay ? "general" : getSegmentType(initial));
  const [parentSegmentId, setParentSegmentId] = useState<string | null>(initial?.parentSegmentId ?? defaultParentSegmentId);
  const [entityId, setEntityId] = useState<string | null>(initial?.entityId || null);
  const [startDate, setStartDate] = useState(
    initial?.startDate
      ? (dayKeyOf(initial.startDate) ?? "")
      : toDateInput(defaultStartDate),
  );
  const [endDate, setEndDate] = useState(
    initial?.endDate
      ? (dayKeyOf(initial.endDate) ?? "")
      : toDateInput(defaultEndDate),
  );
  const [notes, setNotes] = useState(initial?.notes || "");
  const [people, setPeople] = useState<Person[]>(initial?.people || []);
  // null = "inherit" (this segment names nobody of its own).
  const [participants, setParticipants] = useState<string[] | null>(
    initial?.participants && initial.participants.length > 0 ? [...initial.participants] : null,
  );
  const [dayType, setDayType] = useState<CruiseDayType | "">(initial?.dayType ?? "");
  const [portName, setPortName] = useState(initial?.portName || "");
  const [portEntityId, setPortEntityId] = useState<string | null>(initial?.portEntityId || null);

  // Cruise config
  const initialCruise = initial?.cruiseConfig ?? null;
  const [cruiseLineEntityId, setCruiseLineEntityId] = useState<string | null>(initialCruise?.cruiseLineEntityId ?? null);
  const [shipEntityId, setShipEntityId] = useState<string | null>(initialCruise?.shipEntityId ?? null);
  const [shipName, setShipName] = useState<string | null>(null);
  const [homePort, setHomePort] = useState(initialCruise?.homePort ?? "");
  const [nights, setNights] = useState<string>(initialCruise?.nights != null ? String(initialCruise.nights) : "7");

  // The date-building inputs as the form opened, to report whether they moved.
  const [openedDateInputs] = useState(() => ({ startDate, endDate, nights, segmentType }));

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCruise = !isDay && segmentType === "cruise";
  const nightsNum = Math.max(0, Math.floor(Number(nights) || 0));
  const cruiseEndDate = useMemo(
    () => (startDate ? addDaysToInput(startDate, nightsNum) : ""),
    [startDate, nightsNum],
  );

  const suggestedCruiseName = useMemo(() => {
    if (!isCruise) return "";
    const ship = shipName?.trim();
    const when = startDate
      ? new Date(startDate + "T00:00:00").toLocaleDateString(undefined, { month: "long", year: "numeric" })
      : "";
    if (ship && when) return `${ship} — ${when}`;
    if (ship) return ship;
    return "";
  }, [isCruise, shipName, startDate]);
  const effectiveName = !nameTouched && suggestedCruiseName ? suggestedCruiseName : name;

  // "Who was there" — the group that was on this stretch of the trip, which
  // activities under it inherit as their default. `null` means "not set", so
  // the segment keeps taking whatever its parent (or the trip) says.
  const partyIds = useMemo(() => Object.keys(travelParty), [travelParty]);
  const inheritedParticipants = useMemo(() => {
    if (!parentSegmentId) return partyIds;
    return segmentParticipantsLookup(segments, partyIds)(parentSegmentId);
  }, [segments, partyIds, parentSegmentId]);
  const parentName = segments.find((s) => s.id === parentSegmentId)?.name ?? null;
  const memberNames = (ids: string[]) =>
    ids.map((id) => travelParty[id]?.displayName).filter(Boolean).join(", ");

  const handleToggleParticipant = (memberId: string) => {
    const base = participants ?? inheritedParticipants;
    setParticipants(
      base.includes(memberId) ? base.filter((id) => id !== memberId) : [...base, memberId],
    );
  };

  const handleAddPerson = () => {
    setPeople([...people, { role: SEGMENT_ROLE_TYPES[0]?.value || "other", name: "" }]);
  };
  const handleRemovePerson = (index: number) => {
    setPeople(people.filter((_, i) => i !== index));
  };
  const handlePersonChange = (index: number, field: keyof Person, value: string) => {
    setPeople(people.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  const handleEntitySelect = (entity: (Entity & { id: string }) | null) => {
    if (entity) {
      setEntityId(entity.id);
      if (!name && !nameTouched) { setName(entity.displayName); setNameTouched(true); }
    } else {
      setEntityId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const finalName = effectiveName.trim();
    if (!finalName) {
      setError("Name is required.");
      return;
    }
    if (!startDate) {
      setError("Date is required.");
      return;
    }

    let resolvedEnd: string;
    let cruiseConfig: CruiseConfig | null = null;
    if (isCruise) {
      if (nightsNum < 1) {
        setError("Number of nights must be at least 1.");
        return;
      }
      resolvedEnd = cruiseEndDate;
      cruiseConfig = {
        cruiseLineEntityId,
        shipEntityId,
        homePort: homePort.trim() || null,
        nights: nightsNum,
      };
    } else if (isDay) {
      resolvedEnd = startDate;
    } else {
      if (!endDate) {
        setError("Start and end dates are required.");
        return;
      }
      if (new Date(endDate) < new Date(startDate)) {
        setError("End date cannot be before start date.");
        return;
      }
      resolvedEnd = endDate;
    }

    setSaving(true);
    try {
      await onSubmit({
        name: finalName,
        parentSegmentId,
        entityId: isCruise ? shipEntityId : entityId,
        // Calendar days, stored so no phone's zone can move them.
        startDate: dateFromDayKey(startDate),
        endDate: dateFromDayKey(resolvedEnd),
        notes: notes.trim(),
        participants: participants && participants.length > 0 ? participants : null,
        people: people.filter((p) => p.name.trim()),
        segmentType: isDay ? "general" : segmentType,
        cruiseConfig,
        dayType: isDay ? (dayType || null) : null,
        portName: isDay ? (portName.trim() || null) : null,
        portEntityId: isDay ? portEntityId : null,
      }, {
        datesChanged:
          startDate !== openedDateInputs.startDate ||
          endDate !== openedDateInputs.endDate ||
          nights !== openedDateInputs.nights ||
          segmentType !== openedDateInputs.segmentType,
      });
    } catch {
      setError("Failed to save. Please try again.");
      setSaving(false);
    }
  };

  const excludeSet = new Set(excludeSegmentIds);
  const availableSegments = segments.filter((s) => !excludeSet.has(s.id));

  return (
    <form className="form" onSubmit={handleSubmit}>
      {error && <div className="form-error">{error}</div>}

      {!isDay && (
        <label className="form-field">
          <span className="form-label">Segment Type</span>
          <select
            className="form-input"
            value={segmentType}
            onChange={(e) => setSegmentType(e.target.value as SegmentType)}
          >
            {SEGMENT_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
      )}

      {isCruise && (
        <>
          <div className="form-field">
            <span className="form-label">Cruise Line</span>
            <EntityPicker
              types={["DESTINATION"]}
              value={cruiseLineEntityId}
              onChange={(id) => { setCruiseLineEntityId(id); setShipEntityId(null); setShipName(null); }}
              placeholder="Select a cruise line..."
              emptyHint="No cruise lines yet."
            />
            {canManageEntities && (
              <span className="form-hint">
                Missing one? <Link to="/entities/new?type=DESTINATION">Add a cruise line</Link> in the entity library.
              </span>
            )}
          </div>

          <div className="form-field">
            <span className="form-label">Ship</span>
            <EntityPicker
              types={["SHIP"]}
              parentId={cruiseLineEntityId}
              value={shipEntityId}
              onChange={(id, ent) => { setShipEntityId(id); setShipName(ent?.displayName ?? null); }}
              placeholder={cruiseLineEntityId ? "Select a ship..." : "Pick a cruise line first"}
              emptyHint={cruiseLineEntityId ? "No ships for this cruise line yet." : "Pick a cruise line first."}
            />
            {canManageEntities && cruiseLineEntityId && (
              <span className="form-hint">
                Missing one? <Link to={`/entities/new?type=SHIP&parentId=${cruiseLineEntityId}`}>Add a ship</Link> under this cruise line.
              </span>
            )}
          </div>

          <div className="form-row">
            <label className="form-field">
              <span className="form-label">Start Date</span>
              <input type="date" className="form-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
            <label className="form-field">
              <span className="form-label">Nights</span>
              <input type="number" className="form-input" min={1} value={nights} onChange={(e) => setNights(e.target.value)} />
            </label>
          </div>
          {startDate && cruiseEndDate && (
            <p className="form-hint">
              Returns {new Date(cruiseEndDate + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
              {" "}· {nightsNum + 1} itinerary days
            </p>
          )}

          <label className="form-field">
            <span className="form-label">Home Port</span>
            <input
              type="text"
              className="form-input"
              value={homePort}
              onChange={(e) => setHomePort(e.target.value)}
              placeholder="e.g. Port Canaveral, FL"
            />
          </label>
        </>
      )}

      <label className="form-field">
        <span className="form-label">{isDay ? "Day Label" : isCruise ? "Cruise Name" : "Segment Name"}</span>
        <input
          type="text"
          className="form-input"
          value={effectiveName}
          onChange={(e) => { setName(e.target.value); setNameTouched(true); }}
          placeholder={isDay ? "e.g. Day 3 — Nassau" : isCruise ? "e.g. Wonder of the Seas — June 2026" : "e.g. WDW Week, Pre-cruise stay"}
        />
      </label>

      {isDay ? (
        <>
          <div className="form-row">
            <label className="form-field">
              <span className="form-label">Date</span>
              <input type="date" className="form-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
            <label className="form-field">
              <span className="form-label">Day Type</span>
              <select className="form-input" value={dayType} onChange={(e) => setDayType(e.target.value as CruiseDayType | "")}>
                <option value="">— Choose —</option>
                {CRUISE_DAY_TYPES.map((t) => (
                  <option key={t} value={t}>{dayTypeLabel(t)}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="form-field">
            <span className="form-label">Port (optional)</span>
            <input
              type="text"
              className="form-input"
              value={portName}
              onChange={(e) => setPortName(e.target.value)}
              placeholder="e.g. Nassau, Bahamas"
            />
          </label>

          <div className="form-field">
            <span className="form-label">Port Entity (optional)</span>
            <EntityPicker
              types={["LOCATION"]}
              value={portEntityId}
              onChange={(id) => setPortEntityId(id)}
              placeholder="Link a port..."
              emptyHint="No port entities yet."
            />
          </div>
        </>
      ) : !isCruise ? (
        <>
          <div className="form-row">
            <label className="form-field">
              <span className="form-label">Start Date</span>
              <input type="date" className="form-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
            <label className="form-field">
              <span className="form-label">End Date</span>
              <input type="date" className="form-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </label>
          </div>

          <div className="form-field">
            <span className="form-label">Primary Entity (optional)</span>
            <EntityPicker
              types={SEGMENT_ENTITY_TYPES}
              value={entityId}
              onChange={(_id, entity) => handleEntitySelect(entity)}
              placeholder="Search entities..."
            />
          </div>
        </>
      ) : null}

      {availableSegments.length > 0 && (
        <label className="form-field">
          <span className="form-label">Parent Segment</span>
          <select
            className="form-input"
            value={parentSegmentId || ""}
            onChange={(e) => setParentSegmentId(e.target.value || null)}
          >
            <option value="">None (top-level)</option>
            {buildNestedSegmentOptions(availableSegments).map(({ id, label }) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </select>
        </label>
      )}

      <div className="form-field">
        <span className="form-label">Notes</span>
        <RichTextEditor
          value={notes}
          onChange={setNotes}
          placeholder={isDay ? "Notes for this day..." : "Any notes about this segment..."}
        />
      </div>

      {partyIds.length > 0 && (
        <WhoWasThere
          label="Who was there"
          travelParty={travelParty}
          selected={participants ?? inheritedParticipants}
          onToggle={handleToggleParticipant}
          action={
            participants !== null ? (
              <button
                type="button"
                className="btn-link who-here-reset"
                onClick={() => setParticipants(null)}
              >
                Reset
              </button>
            ) : null
          }
          hint={
            participants === null
              ? parentName
                ? `Not set — following ${parentName} (${memberNames(inheritedParticipants)}).`
                : "Not set — everyone on the trip."
              : participants.length === 0
                ? `No one selected — this ${isDay ? "day" : "segment"} will fall back to ${memberNames(inheritedParticipants)}.`
                : "Activities logged here start with these people."
          }
        />
      )}

      {/* People */}
      <div className="form-field">
        <div className="section-header">
          <span className="form-label">{isDay || isCruise ? "Crew / Staff" : "People"}</span>
          <button type="button" className="btn btn-small btn-secondary" onClick={handleAddPerson}>
            + Add
          </button>
        </div>
        {people.map((person, i) => (
          <div key={i} className="people-row">
            <RoleSelect
              value={person.role}
              onChange={(role) => handlePersonChange(i, "role", role)}
              roleTypes={SEGMENT_ROLE_TYPES}
            />
            <input
              type="text"
              className="form-input"
              value={person.name}
              onChange={(e) => handlePersonChange(i, "name", e.target.value)}
              placeholder="Name"
            />
            <button
              type="button"
              className="btn-icon"
              title="Remove"
              onClick={() => handleRemovePerson(i)}
            >
              &times;
            </button>
          </div>
        ))}
      </div>

      <div className="form-actions">
        <button type="submit" className="btn" disabled={saving}>
          {saving ? "Saving..." : submitLabel}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}
